import type {
  Dispatch,
  PropsWithChildren,
  ReactNode,
  SetStateAction,
} from 'react'
import {createRef, useState} from 'react'
import type {Placement} from '@popperjs/core'
import {usePopper} from 'react-popper'
import {useResizeDetector} from 'react-resize-detector'
import {Portal} from 'elements/Portal'
import {useClickOutside} from 'hooks/useClickOutside'
import {useKeyPress} from 'hooks/useKeyPress'
import {cn} from 'utils'

export interface DropdownPropsTargetProps {
  ref: Dispatch<SetStateAction<HTMLElement | null>>
  opened: boolean
  open: () => void
  close: () => void
}

export interface DropdownPropsContentProps {
  close: () => void
}

export interface DropdownProps {
  target: (props: PropsWithChildren<DropdownPropsTargetProps>) => ReactNode
  content: (props: PropsWithChildren<DropdownPropsContentProps>) => ReactNode
  className?: string
  placement?: Placement
  fitContentWidth?: boolean
  clickOutsideToClose?: boolean
  escapeToClose?: boolean
  deferClickOutsideInitialization?: boolean
  onOpen?: () => void
  onClose?: () => void
}

export const Dropdown = ({
  target: inputElement,
  content: contentElement,
  className,
  placement = 'bottom',
  fitContentWidth,
  clickOutsideToClose = true,
  escapeToClose = true,
  deferClickOutsideInitialization = false,
  onOpen,
  onClose,
}: DropdownProps): ReactNode => {
  const [opened, setOpened] = useState(false)
  const [referenceElement, setReferenceElement] = useState<HTMLElement | null>(
    null
  )
  const [popperElement, setPopperElement] = useState<HTMLElement | null>(null)
  const ref = createRef<HTMLDivElement>()

  const {width} = useResizeDetector({targetRef: ref})
  const {styles: popperStyles, attributes} = usePopper(
    referenceElement,
    popperElement,
    {
      placement,
      modifiers: [
        {
          name: 'preventOverflow',
          options: {
            padding: 8,
          },
        },
        {name: 'offset', options: {offset: [0, 5]}},
      ],
    }
  )

  const handleOpen = () => {
    setOpened(true)
    if (onOpen) {
      onOpen()
    }
  }
  const handleClose = () => {
    setOpened(false)
    if (onClose) {
      onClose()
    }
  }

  useClickOutside({
    ref,
    extraRef: popperElement,
    callback: () => {
      if (!clickOutsideToClose) return
      handleClose()
    },
    isOpen: opened,
    deferInitialization: deferClickOutsideInitialization,
  })

  useKeyPress(opened, 'Escape', () => {
    if (!escapeToClose) {
      return
    }
    handleClose()
  })

  return (
    <div
      ref={ref}
      className={cn('outline-none relative inline-block', className)}
    >
      {inputElement({
        ref: setReferenceElement,
        opened,
        open: handleOpen,
        close: handleClose,
      })}

      {opened && (
        <Portal>
          <div
            ref={setPopperElement}
            className='z-[10000] bg-white [&:not(:empty)]:border [&:not(:empty)]:rounded [&:not(:empty)]:shadow-popover'
            style={{
              width: fitContentWidth ? width : undefined,
              ...popperStyles.popper,
            }}
            {...attributes.popper}
          >
            {contentElement({close: handleClose})}
          </div>
        </Portal>
      )}
    </div>
  )
}
